/**
 * Single source of truth for API paths. Never hardcode a URL string inside
 * a service file — add it here and import it instead.
 */
export const endpoints = {
  auth: {
    login: "/auth/login",
    // auth.register removed — POST /auth/register was a @Public() route that
    // created an Active STAFF account with no invite or approval. Staff are
    // created by an admin. Client self-signup is `endpoints.register` below.
    logout: "/auth/logout",
    refresh: "/auth/refresh",
    me: "/auth/me",
    forgotPassword: "/auth/forgot-password",
    resetPassword: "/auth/reset-password",
    acceptInvite: "/auth/accept-invite",
    changePassword: "/auth/change-password",
    profile: "/auth/profile",
    // Interakt WhatsApp OTP — see bizpandit-api/src/auth/auth.controller.ts.
    sendOtp: "/auth/send-otp",
    verifyOtp: "/auth/verify-otp",
    // Secure email-change flow — see AuthService.requestEmailChange/confirmEmailChange.
    changeEmailRequest: "/auth/change-email/request",
    changeEmailConfirm: "/auth/change-email/confirm",
  },
  // Client-portal self-signup — top-level POST /register (RegistrationController).
  register: "/register",
  publicConfig: {
    lovValues: (name: string) => `/public/lov/${encodeURIComponent(name)}/values`,
    lovValuesByField: (entityName: string, fieldName: string) =>
      `/public/lov/field/${encodeURIComponent(entityName)}/${encodeURIComponent(fieldName)}/values`,
  },
  /**
   * Reference data for SIGNED-IN callers — the tenant's own list, falling back
   * to the global one. The `/public/...` pair above reads global only, which
   * is right for the registration form and would empty every dropdown behind
   * the login if it were the only route.
   */
  lovLookup: {
    byField: (entityName: string, fieldName: string) =>
      `/lov-lookup/field/${encodeURIComponent(entityName)}/${encodeURIComponent(fieldName)}/values`,
  },
  dashboard: {
    stats: "/dashboard/stats",
    revenue: "/dashboard/revenue",
    activity: "/dashboard/activity",
  },
  leads: {
    root: "/leads",
    admin: "/leads/admin",
    tagCounts: "/leads/tag-counts",
    byId: (id: string) => `/leads/${id}`,
    convert: (id: string) => `/leads/${id}/convert`,
    /** BP-ISS-20260915-039: convert + record the payment, one action. */
    paymentReceived: (id: string) => `/leads/${id}/payment-received`,
  },
  users: {
    root: "/users",
    staff: "/users/staff",
    /** Suspend / reactivate. Lives under /users, not /access/admin — it is user
     *  management rather than access management. */
    byId: (userId: string) => `/users/${userId}`,
  },
  clients: {
    /** POST the nested create; PATCH the saved client's own details. */
    root: "/clients",
    byId: (contactId: string) => `/clients/${contactId}`,
    /** Portal access for an existing client (contact-scoped, not /users). */
    portalAccess: (contactId: string) => `/clients/${contactId}/portal-access`,
  },
  hr: {
    employees: "/hr/employees",
    employeeOptions: "/hr/employees/options",
    employee: (id: string) => `/hr/employees/${id}`,
    /** Link / unlink the employee's staff login (QA gap report 2026-09-16). */
    employeeLogin: (id: string) => `/hr/employees/${id}/login`,
    employeeRoles: (id: string) => `/hr/employees/${id}/roles`,
    identityReveal: (id: string) => `/hr/employees/${id}/identity/reveal`,
    identity: (id: string) => `/hr/employees/${id}/identity`,
    /** New-hire self-service intake link (BP-ISS-20260919-076). */
    intakeLink: (id: string) => `/hr/employees/${id}/intake-link`,
    intakeLinks: (id: string) => `/hr/employees/${id}/intake-links`,
    compensationReveal: (id: string) => `/hr/employees/${id}/compensation/reveal`,
    compensation: (id: string) => `/hr/employees/${id}/compensation`,
    education: (id: string) => `/hr/employees/${id}/education`,
    educationItem: (id: string, eduId: string) => `/hr/employees/${id}/education/${eduId}`,
    experience: (id: string) => `/hr/employees/${id}/experience`,
    experienceItem: (id: string, expId: string) => `/hr/employees/${id}/experience/${expId}`,
    documents: (id: string) => `/hr/employees/${id}/documents`,
    documentAck: (id: string, docId: string) => `/hr/employees/${id}/documents/${docId}/acknowledge`,
    documentItem: (id: string, docId: string) => `/hr/employees/${id}/documents/${docId}`,
    settings: "/hr/settings",
    setting: (key: string) => `/hr/settings/${key}`,
    // Hiring (phase 2)
    requisitions: "/hr/hiring/requisitions",
    requisition: (id: string) => `/hr/hiring/requisitions/${id}`,
    stages: "/hr/hiring/stages",
    candidates: "/hr/hiring/candidates",
    candidate: (id: string) => `/hr/hiring/candidates/${id}`,
    candidateStage: (id: string) => `/hr/hiring/candidates/${id}/stage`,
    candidateReject: (id: string) => `/hr/hiring/candidates/${id}/reject`,
    candidateHire: (id: string) => `/hr/hiring/candidates/${id}/hire`,
    candidateEducation: (id: string) => `/hr/hiring/candidates/${id}/education`,
    candidateExperience: (id: string) => `/hr/hiring/candidates/${id}/experience`,
    candidateFeedback: (id: string) => `/hr/hiring/candidates/${id}/feedback`,
    // Onboarding (phase 2)
    onboarding: "/hr/onboarding",
    onboardingChecklist: (employeeId: string) => `/hr/onboarding/employees/${employeeId}`,
    onboardingStep: (taskId: string, action: "complete" | "skip" | "reopen") => `/hr/onboarding/steps/${taskId}/${action}`,
    onboardingStepById: (taskId: string) => `/hr/onboarding/steps/${taskId}`,
    // Attendance, shifts, approvals (phase 3)
    attendanceMe: "/hr/attendance/me",
    attendanceCheckIn: "/hr/attendance/check-in",
    attendanceCheckOut: "/hr/attendance/check-out",
    attendanceDay: "/hr/attendance/day",
    attendanceMonth: "/hr/attendance/month",
    attendanceManual: "/hr/attendance/manual",
    attendanceExemptions: "/hr/attendance/exemptions",
    attendanceEvents: (employeeId: string) => `/hr/attendance/employees/${employeeId}/events`,
    shiftTemplates: "/hr/shifts/templates",
    shiftTemplate: (id: string) => `/hr/shifts/templates/${id}`,
    shiftAssignments: "/hr/shifts/assignments",
    approvals: "/hr/approvals",
    approvalDecide: (id: string, action: "approve" | "reject" | "cancel") => `/hr/approvals/${id}/${action}`,
    // Leave (phase 4)
    leaveTypes: "/hr/leave/types",
    leaveType: (id: string) => `/hr/leave/types/${id}`,
    leaveRequests: "/hr/leave/requests",
    leavePreview: "/hr/leave/requests/preview",
    leaveRequestCancel: (id: string) => `/hr/leave/requests/${id}/cancel`,
    leaveBalances: "/hr/leave/balances",
    leaveAdjustments: (employeeId: string) => `/hr/leave/adjustments/${employeeId}`,
    leaveAdjust: "/hr/leave/adjustments",
    holidays: "/hr/leave/holidays",
    // Shift reconciliation (phase 5)
    reconciliationMe: "/hr/reconciliation/me",
    reconciliationItem: (id: string, action: "complete" | "defer" | "escalate") => `/hr/reconciliation/items/${id}/${action}`,
    reconciliationDay: "/hr/reconciliation",
    reconciliationSnapshot: (id: string) => `/hr/reconciliation/${id}/snapshot`,

    // Payroll (phase 7) — screen spec A.11
    payroll: "/hr/payroll",
    payrollHistory: "/hr/payroll/history",
    payrollGenerate: "/hr/payroll/generate",
    payrollLock: (id: string) => `/hr/payroll/${id}/lock`,
    payslipReveal: (id: string) => `/hr/payroll/slips/${id}/reveal`,

    // KRA / KPI (phase 8) — a profile tab, not a screen (screen spec A.12)
    kraScorecard: (employeeId: string) => `/hr/kra/employees/${employeeId}`,
    kraDefine: (assignmentId: string) => `/hr/kra/assignments/${assignmentId}`,
    kraReview: (assignmentId: string) => `/hr/kra/assignments/${assignmentId}/review`,
    // Monthly Pulse (screen spec A.13)
    pulse: "/hr/pulse",
    pulseEmployee: (employeeId: string) => `/hr/pulse/employees/${employeeId}`,
    // Calendar (design §14). The public availability view is deliberately NOT
    // under /hr — it is unauthenticated and addressed by a slug.
    calendarViewable: "/hr/calendar/viewable",
    calendarConnections: "/hr/calendar/connections",
    calendarEvents: "/hr/calendar/events",
    calendarEvent: (id: string) => `/hr/calendar/events/${id}`,
    calendarEventCancel: (id: string) => `/hr/calendar/events/${id}/cancel`,
    calendarWeek: (employeeId: string) => `/hr/calendar/employees/${employeeId}`,
    calendarDelegates: (employeeId: string) => `/hr/calendar/employees/${employeeId}/delegates`,
    calendarDelegate: (employeeId: string, delegateId: string) =>
      `/hr/calendar/employees/${employeeId}/delegates/${delegateId}`,
    calendarPublicToggle: (employeeId: string) => `/hr/calendar/employees/${employeeId}/public`,
    // Separation (design §13, §15.1). The self routes take no id — the subject
    // is the session, so they cannot be pointed at another person's record.
    separations: "/hr/separations",
    separation: (id: string) => `/hr/separations/${id}`,
    separationStep: (id: string, taskId: string) => `/hr/separations/${id}/steps/${taskId}/complete`,
    separationComplete: (id: string) => `/hr/separations/${id}/complete`,
    separationWithdraw: (id: string) => `/hr/separations/${id}/withdraw`,
    separationDeletionRequest: (id: string) => `/hr/separations/${id}/deletion-request`,
    separationAnonymise: (id: string) => `/hr/separations/${id}/anonymise`,
    mySeparation: "/hr/me/separation",
    mySeparationDeletionRequest: "/hr/me/separation/deletion-request",
  },
  // Task attachments (BA 6.1, design 16). NOT under `hr` — an attachment
  // belongs to a task, and who may touch it is decided per task rather than by
  // an HR right.
  taskAttachments: "/task-attachments",
  taskAttachmentUpload: (taskId: string) => `/task-attachments/tasks/${taskId}/upload`,
  taskAttachmentLink: (taskId: string) => `/task-attachments/tasks/${taskId}/link`,
  taskAttachmentReplace: (id: string) => `/task-attachments/${id}/replace`,
  taskAttachmentPurge: (id: string) => `/task-attachments/${id}/purge`,
  taskAttachmentKeep: (id: string) => `/task-attachments/${id}/keep`,
  taskAttachmentDownload: (id: string) => `/task-attachments/${id}/download`,
  // The role dashboards, daily reports, the renewal register and the weekly
  // Performance rollup (Dashboard Feature Spec §2–§4, §6.4).
  // One employee, one dashboard (owner, 2026-09-10). The five module
  // dashboards fold into this; /dashboards/presales and /postsales still
  // exist on the server (the Dashboard spec pins them) but nothing here
  // calls them any more.
  home: "/home",
  dashboards: {
    dailyReportMe: "/daily-reports/me",
    dailyReportPrefill: "/daily-reports/me/prefill",
    // The system-computed day (QA gap §5): own, and another person's under performance:read.
    dayReport: "/daily-reports/day",
    dayReportNotes: "/daily-reports/day/notes",
    userDayReport: (userId: string) => `/daily-reports/users/${userId}/day`,
    // The computed WEEK — the weekly report's pre-fill (BA answer 6).
    weekReport: "/daily-reports/week",
    userWeekReport: (userId: string) => `/daily-reports/users/${userId}/week`,
    dailyReports: "/daily-reports",
    performanceWeekly: "/performance/weekly",
    renewals: "/renewals",
    renewal: (id: string) => `/renewals/${id}`,
    renewalRenew: (id: string) => `/renewals/${id}/renew`,
    renewalReminded: (id: string) => `/renewals/${id}/reminded`,
  },
  // The task engine (Dashboard §6) — phase 2 of the HR build. Named
  // `taskEngine` because an older `tasks` key (delivery step tasks) exists below.
  taskEngine: {
    root: "/tasks",
    summary: "/tasks/summary",
    byId: (id: string) => `/tasks/${id}`,
    status: (id: string) => `/tasks/${id}/status`,
    versions: (id: string) => `/tasks/${id}/versions`,
    review: (id: string) => `/tasks/${id}/review`,
    posted: (id: string) => `/tasks/${id}/posted`,
    assignable: "/tasks/assignable",
    statuses: "/tasks/admin/statuses",
    statusById: (id: string) => `/tasks/admin/statuses/${id}`,
    templates: "/tasks/admin/templates",
    templateById: (id: string) => `/tasks/admin/templates/${id}`,
  },
  notifications: {
    root: "/notifications",
    unreadCount: "/notifications/unread-count",
    read: "/notifications/read",
  },
  accounts: {
    root: "/accounts",
    /** Org-wide person search (scoped, erased hidden) — the Composer's target picker. */
    contactsSearch: "/accounts/contacts/search",
    byId: (id: string) => `/accounts/${id}`,
    contacts: (accountId: string) => `/accounts/${accountId}/contacts`,
    contactById: (accountId: string, contactId: string) =>
      `/accounts/${accountId}/contacts/${contactId}`,
    contactAstrologyProfile: (accountId: string, contactId: string) =>
      `/accounts/${accountId}/contacts/${contactId}/astrology-profile`,
  },
  /**
   * The unified window — one save logs the conversation and books the next
   * action. /activities and /follow-ups remain for reading and editing.
   */
  interactions: {
    root: "/interactions",
    history: "/interactions/history",
    dispositions: "/interactions/dispositions",
    reasons: "/interactions/reasons",
    /** What the rule would book, run through the same working calendar as the save. */
    preview: "/interactions/preview",
    openWork: "/interactions/open-work",
    /* V2: the record's tag — the drawer's selector, and what resolves its rules. */
    tags: "/interactions/tags",
    setTag: "/interactions/tag",
    notDone: (followUpId: string) => `/interactions/follow-ups/${followUpId}/not-done`,
    /* V2: the threads one person carries, and what each of them governs. */
    threads: "/interactions/threads",
    autoPlan: "/interactions/auto-plan",
    enquiries: "/interactions/enquiries",
    enquiryCatalogue: "/interactions/enquiries/catalogue",
    enquiryTag: (id: string) => `/interactions/enquiries/${id}/tag`,
    closeEnquiry: (id: string) => `/interactions/enquiries/${id}/close`,
    /* BP-ISS-20261002-104: a client's two kinds of conversation, and the Post-Sales list (-103). */
    relationshipStream: "/interactions/streams/relationship",
    interestStreams: "/interactions/streams/interests",
    newInterests: "/interactions/new-interests",
  },
  activities: {
    root: "/activities",
    byId: (id: string) => `/activities/${id}`,
  },
  followUps: {
    root: "/follow-ups",
    stats: "/follow-ups/stats",
  },
  /**
   * Phones and addresses as records of their own — the write path the
   * "Phones & Addresses" tab is built on. Before this the tab was a read-only
   * mirror of Account/Contact's flat columns, and ContactPhone/Address/
   * AddressLink had never been written to.
   */
  contactPhones: {
    root: (contactId: string) => `/contacts/${contactId}/phones`,
    byId: (contactId: string, phoneId: string) => `/contacts/${contactId}/phones/${phoneId}`,
  },
  addresses: {
    root: "/addresses",
    byId: (addressId: string) => `/addresses/${addressId}`,
  },
  proposals: {
    root: "/proposals",
    byId: (id: string) => `/proposals/${id}`,
  },
  clientServices: {
    root: "/client-services",
    byId: (id: string) => `/client-services/${id}`,
    steps: (id: string) => `/client-services/${id}/steps`,
    stepById: (id: string, stepProgressId: string) =>
      `/client-services/${id}/steps/${stepProgressId}`,
    status: (id: string) => `/client-services/${id}/status`,
  },
  invoices: {
    root: "/invoices",
    byId: (id: string) => `/invoices/${id}`,
  },
  tasks: {
    root: "/tasks",
    byId: (id: string) => `/tasks/${id}`,
  },
  numerology: {
    numberAnalysis: "/numerology/number-analysis",
    businessNameAnalysis: "/numerology/business-name-analysis",
    // Authenticated re-runs, quota-gated — see NumerologyQuotaService.
    reRunNumberAnalysis: "/numerology/re-run/number-analysis",
    reRunBusinessNameAnalysis: "/numerology/re-run/business-name-analysis",
  },
  signupRequests: {
    root: "/admin/signup-requests",
    byId: (id: string) => `/admin/signup-requests/${id}`,
    approve: (id: string) => `/admin/signup-requests/${id}/approve`,
    reject: (id: string) => `/admin/signup-requests/${id}/reject`,
    requestMoreInfo: (id: string) => `/admin/signup-requests/${id}/request-more-info`,
  },
  packageApprovals: {
    root: "/admin/package-approvals",
    stats: "/admin/package-approvals/stats",
    byId: (id: string) => `/admin/package-approvals/${id}`,
    approve: (id: string) => `/admin/package-approvals/${id}/approve`,
    reject: (id: string) => `/admin/package-approvals/${id}/reject`,
    requestMoreInfo: (id: string) => `/admin/package-approvals/${id}/request-more-info`,
  },
  pricing: {
    discountTiers: "/pricing/discount-tiers",
    discountTierById: (id: string) => `/pricing/discount-tiers/${id}`,
    paymentSettings: "/pricing/payment-settings",
    gstMode: "/pricing/gst-mode",
  },
  followupSettings: {
    root: "/followup-settings",
  },
  /**
   * Settings §2, §3, §5, §6, §7. §1 stays on `followupSettings` above — it is
   * the same table the interaction window reads, and moving the route would
   * not move the owner.
   */
  settings: {
    root: "/settings",
    holidays: "/settings/holidays",
    holiday: (id: string) => `/settings/holidays/${id}`,
    featureOverride: (key: string) => `/settings/features/${key}/override`,
    /* V2 §Tags & cadence */
    tags: "/settings/tags",
    tag: (id: string) => `/settings/tags/${id}`,
    tagRules: (id: string) => `/settings/tags/${id}/rules`,
    tagRulesCopy: (id: string) => `/settings/tags/${id}/rules/copy`,
    tagPlans: (id: string) => `/settings/tags/${id}/plans`,
    tagEntries: (id: string) => `/settings/tags/${id}/entry-dispositions`,
    /* V2 §Message sequences */
    sequences: "/settings/sequences",
    sequence: (id: string) => `/settings/sequences/${id}`,
    sequenceSteps: (id: string) => `/settings/sequences/${id}/steps`,
    sequenceStep: (id: string, position: number) => `/settings/sequences/${id}/steps/${position}`,
    sequenceStepMove: (id: string) => `/settings/sequences/${id}/steps/move`,
    /* V2 §Message sequences — manual template add/edit/deactivate/delete */
    templates: "/settings/sequences/templates",
    template: (id: string) => `/settings/sequences/templates/${id}`,
    templateReactivate: (id: string) => `/settings/sequences/templates/${id}/reactivate`,
    templateHardDelete: (id: string) => `/settings/sequences/templates/${id}/hard`,
  },
  roles: {
    root: "/roles",
    permissions: "/permissions",
    rolePermissions: (roleId: string) => `/roles/${roleId}/permissions`,
  },
  /** Access Control admin console (RBAC v2). */
  accessAdmin: {
    stats: "/access/admin/stats",
    roles: "/access/admin/roles",
    roleById: (roleId: string) => `/access/admin/roles/${roleId}`,
    roleMatrix: (roleId: string) => `/access/admin/roles/${roleId}/matrix`,
    objects: "/access/admin/objects",
    roleObjects: (roleId: string) => `/access/admin/roles/${roleId}/objects`,
    users: "/access/admin/users",
    assignableRoles: "/access/admin/assignable-roles",
    userRoles: (userId: string) => `/access/admin/users/${userId}/roles`,
    simulate: "/access/admin/simulate",
    audit: "/access/admin/audit",
  },
  lov: {
    root: "/lov",
    byId: (id: string) => `/lov/${id}`,
    values: (lovId: string) => `/lov/${lovId}/values`,
    valueById: (lovId: string, valueId: string) => `/lov/${lovId}/values/${valueId}`,
    byField: (entityName: string, fieldName: string) =>
      `/lov/field/${encodeURIComponent(entityName)}/${encodeURIComponent(fieldName)}`,
  },
  items: {
    root: "/items",
    byId: (id: string) => `/items/${id}`,
  },
  services: {
    root: "/services",
    designations: "/services/designations",
    byId: (id: string) => `/services/${id}`,
    steps: (serviceId: string) => `/services/${serviceId}/steps`,
    stepById: (serviceId: string, stepId: string) =>
      `/services/${serviceId}/steps/${stepId}`,
  },
  packages: {
    root: "/packages",
    byId: (id: string) => `/packages/${id}`,
    sellables: (packageId: string) => `/packages/${packageId}/sellables`,
    sellableById: (packageId: string, sellableId: string) =>
      `/packages/${packageId}/sellables/${sellableId}`,
    tranches: (packageId: string) => `/packages/${packageId}/tranches`,
    trancheById: (packageId: string, trancheId: string) =>
      `/packages/${packageId}/tranches/${trancheId}`,
  },
  events: {
    root: "/events",
    byId: (id: string) => `/events/${id}`,
    highlightedPackages: (eventId: string) => `/events/${eventId}/highlighted-packages`,
    highlightedPackageById: (eventId: string, highlightedPackageId: string) =>
      `/events/${eventId}/highlighted-packages/${highlightedPackageId}`,
  },
  client: {
    dashboard: "/client/dashboard",
    profile: "/client/profile",
    // Self-service profile sections (BP-ISS-20260731-016 / -018) — the
    // subject is the session's own contact, no id in the path.
    profileBusinessDetails: "/client/profile/business-details",
    profileEducation: "/client/profile/education",
    profileEducationById: (id: string) => `/client/profile/education/${id}`,
    profileSkills: "/client/profile/skills",
    profileMembership: "/client/profile/membership",
    /** Birth self-correction — sealed, audited, Kundali auto-regenerated. */
    profileBirthDetails: "/client/profile/birth-details",
    profileContactDetails: "/client/profile/contact-details",
    businessDetails: "/client/business-details",
    astrologyProfile: "/client/astrology-profile",
    community: "/client/community",
    communityById: (id: string) => `/client/community/${id}`,
    referrals: "/client/referrals",
    dreamAsks: "/client/referrals/asks",
    dreamAskById: (id: string) => `/client/referrals/asks/${id}`,
    dreamGives: "/client/referrals/gives",
    dreamGiveById: (id: string) => `/client/referrals/gives/${id}`,
    astroUpdates: "/client/astro-updates",
    remedies: "/client/remedies",
    dasha: "/client/dasha",
    experiences: "/client/experiences",
    experienceById: (id: string) => `/client/experiences/${id}`,
    educations: "/client/educations",
    educationById: (id: string) => `/client/educations/${id}`,
    skills: "/client/skills",
    skillById: (id: string) => `/client/skills/${id}`,
    documents: "/client/documents",
    documentById: (id: string) => `/client/documents/${id}`,

    // Refer & Earn + numerology re-run quota. Mounted at client/me/* — separate
    // from client/referrals/* (Dream Ask / Dream Give feature above).
    me: {
      referralLink: "/client/me/referral-link",
      referrals: "/client/me/referrals",
      reportQuota: "/client/me/report-quota",
      reportHistory: "/client/me/report-history",
    },
  },
  payment: {
    createOrder: "/payment/create-order",
    verifyPayment: "/payment/verify-payment",
    cashOrder: "/payment/cash-order",
    csrPercent: "/payment/csr-percent",
    history: "/payment/history",
    byId: (id: string) => `/payment/${id}`,
    adminOverview: "/payment/admin/overview",
    adminOrderDueDate: (orderId: string) => `/payment/admin/orders/${orderId}/due-date`,
    // Correcting a mis-typed amount on a manually recorded payment
    // (BP-ISS-20260916-043) — payment:correct, and its history under payment:read.
    adminOrderAmount: (orderId: string) => `/payment/admin/orders/${orderId}/amount`,
    adminOrderCorrections: (orderId: string) => `/payment/admin/orders/${orderId}/corrections`,
  },
  uploads: {
    avatar: "/uploads/avatar",
    document: "/uploads/document",
  },
  reports: {
    root: "/admin/reports",
  },
  auditLog: {
    root: "/audit-log",
    /** Background job health — same controller, same audit:read gate. */
    jobHealth: "/audit-log/job-health",
  },
  bizcalendar: {
    list: "/bizcalendars",
    current: "/bizcalendars/current",
    byId: (id: string) => `/bizcalendars/${id}`,
    state: (id: string) => `/bizcalendars/${id}/state`,
    actionState: (id: string) => `/bizcalendars/${id}/action-state`,
    customActions: (id: string) => `/bizcalendars/${id}/custom-actions`,
    customActionById: (id: string, customActionId: string) =>
      `/bizcalendars/${id}/custom-actions/${customActionId}`,
    meetings: (id: string) => `/bizcalendars/${id}/meetings`,
    dailyReview: (id: string) => `/bizcalendars/${id}/daily-review`,

    // Staff review. Everything above is the CLIENT's own calendar and resolves
    // the contact from the session; these four are gated on bizcalendar:read /
    // bizcalendar:approve instead. Split backend-side on 2026-08-21 — the
    // review screen used to call `byId` above, which answered 403 for staff
    // (both staff accounts have User.contact_id = NULL) and filtered to
    // published-only anyway, so a draft could never be opened for review.
    reviewQueue: "/bizcalendars/admin/review-queue",
    adminById: (id: string) => `/bizcalendars/admin/${id}`,
    publish: (id: string) => `/bizcalendars/admin/${id}/publish`,
    reject: (id: string) => `/bizcalendars/admin/${id}/reject`,
  },
  whatsappGroups: {
    root: "/admin/whatsapp-groups",
    byId: (id: string) => `/admin/whatsapp-groups/${id}`,
    reorder: "/admin/whatsapp-groups/reorder",
  },
  astroUpdates: {
    // Real since 2026-09-10 (src/astro-update in bizpandit-v2) — the Composer
    // publishes here and the portal reads /client/astro-updates. Both were
    // frontend-only 404s from 2026-07-28 until then.
    root: "/astro-updates",
    byId: (id: string) => `/astro-updates/${id}`,
    publish: (id: string) => `/astro-updates/${id}/publish`,
  },
  remedies: {
    root: "/remedies",
    byId: (id: string) => `/remedies/${id}`,
  },
  remedyCatalog: {
    root:       "/remedy-catalog",
    byId:       (id: string) => `/remedy-catalog/${id}`,
    copy:       (id: string) => `/remedy-catalog/${id}/copy`,
    media:      (id: string) => `/remedy-catalog/${id}/media`,
  },
  clientRemedy: {
    root:   "/client-remedy",
    byId:   (id: string) => `/client-remedy/${id}`,
  },
  impersonate: {
    byContact: (contactId: string) => `/auth/impersonate/by-contact/${contactId}`,
    exit:      "/auth/impersonate/exit",
  },
  // Refer & Earn admin endpoints — see ReferralsModule (src/referrals/).
  admin: {
    referrals: "/admin/referrals",
    reportLimits: "/admin/report-limits",
    // Tenants — Platform Admin only.
    organizations: "/admin/organizations",
    organization: (id: string) => `/admin/organizations/${id}`,
    userReportLimits: (id: string) => `/admin/users/${id}/report-limits`,
  },
  // Account erasure — 07-ACCOUNT-LIFECYCLE.md. Two doors, one purge.
  accountSettings: {
    erasureEligibility: "/portal/account/erasure-eligibility",
    deactivate:         "/portal/account/deactivate",
    eraseStart:         "/portal/account/erase/start",
    erase:              "/portal/account/erase",

    // A18 — the data principal's own consent. Distinct from /consent/*, which
    // is the staff surface and refuses a CLIENT audience. No id in either path:
    // the subject is the session.
    consent:            "/portal/account/consent",
    consentWithdraw:    "/portal/account/consent/withdraw",

    // There is deliberately NO client-facing data-export endpoint. A client may
    // view their data in the app and may delete it, but may not download a
    // copy. The §6 access right is answered by staff via
    // /admin/data-rights/export, which is gated on data_rights:export.
  },
  dataErasure: {
    root:    "/admin/data-erasure",
    lookup:  "/admin/data-erasure/lookup",
    approve: (id: string) => `/admin/data-erasure/${id}/approve`,
    reject:  (id: string) => `/admin/data-erasure/${id}/reject`,
    cancel:  (id: string) => `/admin/data-erasure/${id}/cancel`,
  },
  // Workflow & Delivery — the projects list and the unified worklist.
  delivery: {
    projects:        "/delivery/projects",
    projectStats:    "/delivery/projects/stats",
    // The gate. These three need delivery:approve, not delivery:read.
    managers:        "/delivery/managers",
    pendingApproval: "/delivery/projects/pending-approval",
    approveDelivery: (id: string) => `/delivery/projects/${id}/approve-delivery`,
    rejectDelivery:  (id: string) => `/delivery/projects/${id}/reject-delivery`,
    // One project, in depth.
    project:         (id: string) => `/delivery/projects/${id}`,
    projectActivity: (id: string) => `/delivery/projects/${id}/activity`,
    projectStatus:   (id: string) => `/delivery/projects/${id}/status`,
    projectManager:  (id: string) => `/delivery/projects/${id}/manager`,
    projectTasks:    (id: string) => `/delivery/projects/${id}/tasks`,
    projectMilestones: (id: string) => `/delivery/projects/${id}/milestones`,
    projectAlerts:   (id: string) => `/delivery/projects/${id}/alerts`,
    deliverables:    (id: string) => `/delivery/projects/${id}/deliverables`,
    deliverableVisibility: (id: string) => `/delivery/deliverables/${id}/visibility`,
    // The task drawer.
    task:            (id: string) => `/delivery/tasks/${id}`,
    taskComments:    (id: string) => `/delivery/tasks/${id}/comments`,
    // The inbox and the board.
    alerts:          "/delivery/alerts",
    alertSnooze:     (id: string) => `/delivery/alerts/${id}/snooze`,
    alertDismiss:    (id: string) => `/delivery/alerts/${id}/dismiss`,
    alertRetry:      (id: string) => `/delivery/alerts/${id}/retry`,
    dashboard:       "/delivery/dashboard",
  },
  myWork: {
    root:    "/me/work",
    summary: "/me/work/summary",
    forUser: (id: string) => `/users/${id}/work`,
  },
} as const;
